import React, { createContext, useContext } from 'react'
import { t, Trans } from '@lingui/macro'

import Spinner from '../../../../components/common/loader/Loader'

import {
  SurveyQuestionWithAnswersFragment,
  useSurveyResultsQuery
} from './SurveyResults.codegen'
import SurveyResultsDownloadCSV from './SurveyResultsDownloadCSV'
import SurveyResultsFilter from './SurveyResultsFilter'
import SurveyResultsQuestions from './SurveyResultsQuestions'
import { ManageSurveyContext } from '../ManageSurveyContext'
import IconEmptyResources from 'public/assets/img/empty-resources.svg'
import {
  useDateFilterContext,
  WithDateFilterContext
} from '../../../../components/common/dateFilter'
import { endOfDay, startOfDay } from 'date-fns'

export const SurveyResultsContext = createContext<{
  questionsWithResult: SurveyQuestionWithAnswersFragment[]
  surveyId: number
  refetch: () => Promise<any>
}>(null as any)

const SurveysResultsComponent = () => {
  const { survey } = useContext(ManageSurveyContext)
  const [filters] = useDateFilterContext()

  const { data, refetch } = useSurveyResultsQuery({
    variables: {
      id: survey.id,
      filters: {
        from: filters.from.toISOString(),
        to: filters.to.toISOString()
      }
    }
  })
  // need to use plain query + refetch, otherwise unit tests will fail
  const notFirstRender = React.useRef(false)

  React.useEffect(() => {
    if (notFirstRender.current) {
      refetch()
    } else {
      notFirstRender.current = true
    }
  }, [filters])

  if (!data) {
    return <Spinner />
  }

  const questionsWithResult = data.admin?.survey?.questionsWithoutInfoType
  const surveyId = data.admin?.survey?.id

  if (!surveyId) {
    return null
  }
  if (!questionsWithResult || questionsWithResult?.length === 0) {
    return (
      <div className="no-items">
        <img src={IconEmptyResources} alt={t`Empty resources`} />
        <h3>
          <Trans>No questions found</Trans>
        </h3>
      </div>
    )
  }

  return (
    <SurveyResultsContext.Provider
      value={{ questionsWithResult, surveyId, refetch }}
    >
      <SurveyResultsFilter />
      <SurveyResultsQuestions />
      <div className="SurveyResultsDownloadCSV__wrapper">
        <SurveyResultsDownloadCSV />
      </div>
    </SurveyResultsContext.Provider>
  )
}

export const SurveysResults = (props) => {
  const { survey } = useContext(ManageSurveyContext)
  const publishedAt = survey.published_at

  const defaultFrom = React.useMemo(
    () => startOfDay(publishedAt ? new Date(publishedAt) : new Date()),
    [publishedAt]
  )

  return (
    <WithDateFilterContext
      defaultValues={{
        from: defaultFrom,
        to: endOfDay(new Date())
      }}
    >
      <SurveysResultsComponent {...props} />
    </WithDateFilterContext>
  )
}
